import { createReducer } from '@reduxjs/toolkit';
import forEach from 'lodash/forEach';
import find from 'lodash/find';
import sortBy from 'lodash/sortBy';
import isEmpty from 'lodash/isEmpty';
import head from 'lodash/head';
import {
  currentCarpetPadChoices,
  getSelectedCarpetPad,
  updateFetchedPadChoices,
  setSelectedCarpetPad,
  removePreselectionFlag,
} from '../actions';
import { fromSavedDesign } from '../actions/pathActions';

export const initialState = {
  // array of pad hdmsskus keyed by choice hdmssku
  currentCarpetChoices: {},
  // full pad choice data keyed by pad hdmssku
  fetchedPadsByChoice: {},
  // currently selected pad choice
  selectedCarpetPad: {},
  loadPreselections: true,
};

const urlInitial = typeof window !== 'undefined' ? decodeURIComponent(document.baseURI) : '';

const isCompatiblePadChoice = (selectedCarpetPad, compatiblePadChoices) => {
  return !isEmpty(selectedCarpetPad) &&
    compatiblePadChoices.includes(selectedCarpetPad.padHDMSSku);
};
const isNotCompatiblePadChoice = (selectedCarpetPad, compatiblePadChoices) => {
  return !isEmpty(selectedCarpetPad) &&
    !compatiblePadChoices.includes(selectedCarpetPad.padHDMSSku);
};
const findRecommendedPadSku = (padSkusWithChoiceType) => {
  return find(padSkusWithChoiceType, (ch) => ch.choiceType === 'Recommended').sku;
};

export const carpetPad = createReducer(initialState,
  (builder) => builder
    .addCase(setSelectedCarpetPad, (state, action) => ({ ...state, selectedCarpetPad: { ...action.payload } }))
    .addCase(removePreselectionFlag, (state) => ({ ...state, loadPreselections: false }))
    .addCase(currentCarpetPadChoices, (state) => {
      const { fetchedPadsByChoice } = state;
      let currentSelectedPad = find(fetchedPadsByChoice, (ch) => ch.isSelected);
      if (!currentSelectedPad) {
        // selected pad is empty take the first fetchedPad
        const sortedPadChoices = sortBy(fetchedPadsByChoice, (cp) => cp.padUIorder);
        const firstChoiceKey = Object.keys(sortedPadChoices)[0];
        currentSelectedPad = sortedPadChoices[firstChoiceKey];
      }
      return { ...state, selectedCarpetPad: { ...currentSelectedPad } };
    })
    .addCase(getSelectedCarpetPad, (state, action) => {
      const { id } = action.payload;
      const newState = {};
      forEach(state.fetchedPadsByChoice, (val, key) => {
        newState[key] = { ...val, isSelected: val.id === id };
      });
      return {
        ...state,
        fetchedPadsByChoice: { ...newState },
        selectedCarpetPad: { ...action.payload },
      };
    })
    .addCase(updateFetchedPadChoices, (state, action) => {
      const { payload: {
        padChoices, // modified choices from configurator choices node
        padSkusByHDMSsku,
        compatiblePadSkus,
      } } = action;

      let selectedChoice = { ...state.selectedCarpetPad };

      if (fromSavedDesign(urlInitial) && state.loadPreselections) {
        selectedChoice = find(padChoices, (ch) => ch.isSelected);
      } else {
        // if carpetPad is from url, we set with missing parameters
        // [padChoice, padDescription, padPrice, padUIorder, padHDMSSku]
        // we need to use the updated one from padChoices
        selectedChoice = find(padChoices, (ch) => ch.id === selectedChoice.id);
        // CASE 1: there is a preselected pad compatible with the new choice
        if (isCompatiblePadChoice(selectedChoice, compatiblePadSkus)) {
          const preselectionId = state.selectedCarpetPad.id;
          selectedChoice = find(padChoices, (choice) => choice.id === preselectionId);
        }
        // CASE 2: there is a preselected pad but not compatible with the new choice
        if (isNotCompatiblePadChoice(selectedChoice, compatiblePadSkus)) {
          const recommendedPadSku = findRecommendedPadSku(head(Object.values(padSkusByHDMSsku)));
          selectedChoice = padChoices[recommendedPadSku];
        }
      }

      return {
        ...state,
        currentCarpetChoices: { ...state.currentCarpetChoices, ...padSkusByHDMSsku },
        fetchedPadsByChoice: { ...padChoices },
        selectedCarpetPad: { ...selectedChoice },
      };
    })
);